import { DOCUMENT } from '@angular/common';
import {
  DestroyRef,
  Injectable,
  InjectionToken,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { OverlayContainer } from '@angular/cdk/overlay';
import { Store } from '@ngrx/store';
import { toSignal } from '@angular/core/rxjs-interop';
import { selectThemeToggle } from '@app/generated/feature-flags.selectors';
import { AppConfigService } from '../config/app-config.service';
import {
  THEME_COOKIE_NAME_STORAGE_KEY,
  ThemeCookieConfig,
  parseThemeMode,
  readThemeCookie,
  resolveThemeCookieConfig,
  serializeThemeCookie,
} from './theme-cookie';

export type ThemeMode = 'light' | 'dark' | 'system';

export const THEME_STORAGE_KEY = 'syrf-theme-mode';
export const THEME_TOGGLE_STORAGE_KEY = 'syrf-theme-toggle-enabled';
export const DARK_THEME_CLASS = 'global-dark-theme';

/**
 * Access to the page's cookies, so specs can observe exactly what is written
 * (jsdom silently drops `Secure` cookies on its http test origin).
 */
export interface ThemeCookieJar {
  read(): string;
  write(cookie: string): void;
}

export const THEME_COOKIE_JAR = new InjectionToken<ThemeCookieJar>(
  'THEME_COOKIE_JAR',
  {
    providedIn: 'root',
    factory: () => {
      const document = inject(DOCUMENT);
      return {
        read: () => document.cookie ?? '',
        write: (cookie: string) => {
          document.cookie = cookie;
        },
      };
    },
  }
);

@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly _document = inject(DOCUMENT);
  private readonly _overlayContainer = inject(OverlayContainer);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _store = inject(Store, { optional: true });
  private readonly _cookieJar = inject(THEME_COOKIE_JAR);

  /**
   * The shared theme preference cookie (account portal slice 3), validated
   * against this host; null when the deployment's name is unusable, in which
   * case the preference stays in this origin's localStorage only.
   */
  private readonly _cookieConfig: ThemeCookieConfig | null =
    this._resolveCookieConfig();

  private readonly _storeThemeToggle = this._store
    ? toSignal(this._store.select(selectThemeToggle), {
        initialValue: AppConfigService.configLoaded
          ? AppConfigService.staticConfig.themeToggle
          : false,
      })
    : signal(
        AppConfigService.configLoaded
          ? AppConfigService.staticConfig.themeToggle
          : false
      );

  /** Whether the theme toggle feature is enabled */
  readonly isFeatureEnabled = computed<boolean>(() => {
    return this._storeThemeToggle() ?? false;
  });

  private readonly _mode = signal<ThemeMode>(this._loadInitialMode());
  private readonly _systemPrefersDark = signal<boolean>(
    this._getSystemPrefersDark()
  );

  /** Active mode setting: 'light', 'dark', or 'system' */
  readonly mode = this._mode.asReadonly();

  /** Whether dark mode is currently effective */
  readonly isDark = computed<boolean>(() => {
    if (!this.isFeatureEnabled()) {
      return false;
    }
    const currentMode = this._mode();
    if (currentMode === 'dark') return true;
    if (currentMode === 'light') return false;
    return this._systemPrefersDark();
  });

  /** Display label for the current mode */
  readonly modeLabel = computed<string>(() => {
    switch (this._mode()) {
      case 'dark':
        return 'Dark';
      case 'light':
        return 'Light';
      case 'system':
        return 'System';
    }
  });

  constructor() {
    this._initSystemListener();

    // Immediately apply theme state to document and overlay container synchronously
    this._applyTheme(this.isDark());

    // Carry an existing choice into the shared cookie (3.17): a person who
    // picked a mode before the cookie existed keeps it on Identity too. Only
    // while the flag is on, because themeToggle gates the cookie on both sites.
    effect(() => {
      if (this.isFeatureEnabled()) {
        this._migrateStoredChoiceToCookie();
      }
    });

    // Synchronize the DOM and effective flag storage whenever theme state changes
    effect(() => {
      const dark = this.isDark();
      this._applyTheme(dark);
      try {
        if (typeof window !== 'undefined' && window.localStorage) {
          window.localStorage.setItem(
            THEME_TOGGLE_STORAGE_KEY,
            this.isFeatureEnabled() ? 'true' : 'false'
          );
        }
      } catch {
        // Ignore storage errors
      }
    });
  }

  /**
   * Set explicit theme mode and persist it: to localStorage, and (with the
   * flag on) to the shared theme cookie so Identity's next page matches.
   * Anything but an allowlisted mode is refused.
   */
  setMode(mode: ThemeMode): void {
    if (parseThemeMode(mode) === null) {
      return;
    }
    this._mode.set(mode);
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        window.localStorage.setItem(THEME_STORAGE_KEY, mode);
      }
    } catch {
      // Ignore storage errors (e.g. private browsing mode)
    }
    if (this.isFeatureEnabled()) {
      this._writeCookie(mode);
    }
  }

  /**
   * Toggle between light and dark mode.
   * If currently in system mode, switches to opposite of effective state.
   */
  toggleTheme(): void {
    if (this.isDark()) {
      this.setMode('light');
    } else {
      this.setMode('dark');
    }
  }

  /**
   * Precedence (the same as the index.html pre-paint script): the shared
   * cookie, then this origin's localStorage, then the system preference.
   */
  private _loadInitialMode(): ThemeMode {
    const fromCookie = this._readCookie();
    if (fromCookie !== null) {
      return fromCookie;
    }
    return this._readStoredMode() ?? 'system';
  }

  private _readStoredMode(): ThemeMode | null {
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        return parseThemeMode(window.localStorage.getItem(THEME_STORAGE_KEY));
      }
    } catch {
      // Ignore storage errors
    }
    return null;
  }

  private _resolveCookieConfig(): ThemeCookieConfig | null {
    if (!AppConfigService.configLoaded) {
      return null;
    }
    const config = AppConfigService.staticConfig;
    const resolved = resolveThemeCookieConfig({
      name: config.themeCookieName,
      domain: config.themeCookieDomain,
      hostname: this._document?.location?.hostname ?? '',
      runtimeEnvironment: config.runtimeEnvironment,
    });
    // The pre-paint script in index.html runs before the configuration is
    // fetched, so it learns the deployment's cookie name from here.
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        if (resolved) {
          window.localStorage.setItem(THEME_COOKIE_NAME_STORAGE_KEY, resolved.name);
        } else {
          window.localStorage.removeItem(THEME_COOKIE_NAME_STORAGE_KEY);
        }
      }
    } catch {
      // Ignore storage errors
    }
    return resolved;
  }

  private _readCookie(): ThemeMode | null {
    if (!this._cookieConfig) {
      return null;
    }
    try {
      return readThemeCookie(this._cookieJar.read(), this._cookieConfig.name);
    } catch {
      return null;
    }
  }

  private _writeCookie(mode: ThemeMode): void {
    if (!this._cookieConfig) {
      return;
    }
    try {
      this._cookieJar.write(serializeThemeCookie(this._cookieConfig, mode));
    } catch {
      // A blocked cookie leaves this origin's localStorage choice in place.
    }
  }

  private _migrateStoredChoiceToCookie(): void {
    if (!this._cookieConfig || this._readCookie() !== null) {
      return;
    }
    const stored = this._readStoredMode();
    if (stored !== null) {
      this._writeCookie(stored);
    }
  }

  private _getSystemPrefersDark(): boolean {
    if (
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function'
    ) {
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return false;
  }

  private _initSystemListener(): void {
    if (
      typeof window === 'undefined' ||
      typeof window.matchMedia !== 'function'
    ) {
      return;
    }

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent | MediaQueryList) => {
      this._systemPrefersDark.set(e.matches);
    };

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', handler);
      this._destroyRef.onDestroy(() => {
        mediaQuery.removeEventListener('change', handler);
      });
    } else if (typeof (mediaQuery as any).addListener === 'function') {
      (mediaQuery as any).addListener(handler);
      this._destroyRef.onDestroy(() => {
        (mediaQuery as any).removeListener(handler);
      });
    }
  }

  private _applyTheme(isDark: boolean): void {
    const docEl = this._document?.documentElement;
    const body = this._document?.body;

    if (docEl) {
      docEl.classList.remove('syrf-theme-pending');
      docEl.classList.toggle(DARK_THEME_CLASS, isDark);
      docEl.style.colorScheme = isDark ? 'dark' : 'light';
    }

    if (body) {
      body.classList.toggle(DARK_THEME_CLASS, isDark);
    }

    const overlayEl = this._overlayContainer?.getContainerElement();
    if (overlayEl) {
      overlayEl.classList.toggle(DARK_THEME_CLASS, isDark);
    }

    const metaThemeColor = this._document?.querySelector('meta[name="theme-color"]');
    if (metaThemeColor) {
      metaThemeColor.setAttribute('content', isDark ? '#1b1f28' : '#ffffff');
    }
  }
}
